/**
 * Service catalogue admin — Steps 64/65/68, rebuilt to the REFERENCE
 * anatomy mined from Kai's sandbox captures (docs/service-catalogue-parity.md):
 *
 * LIST = two panes: a category tree sidebar (subcategories to 3 levels,
 * inline rename/add/delete, a pinned "All service items" node with counts)
 * and an item table (search, Published/Drafts filter, status pill, hover
 * Clone) — the reference's /catalog/items screen, in our tokens.
 *
 * EDITOR = one scrolling page under a STICKY action header carrying the
 * item's Draft/Published status pill and a split Save (Save & Publish /
 * Save as Draft / Save As-clone) — one save action covers the item's
 * details AND its form (the builder exposes an imperative save handle).
 * Drafts are catalogue_items.isActive=0: hidden from requester browse,
 * unsubmittable — one lifecycle flag, presented the reference's way.
 * Field properties open in a right slide-over (Sheet), not inline.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Copy,
  FolderInput,
  FolderPlus,
  LayoutGrid,
  ListPlus,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { catalogueApi, type CatalogueCategory, type CatalogueItem } from '../../features/fields/api';
import { FormLayoutEditor, type FormLayoutEditorHandle } from '../../features/fields/FormLayoutEditor';
import { CatalogueIcon, CATALOGUE_ICONS } from '../../features/fields/catalogueIcons';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { RichTextEditor } from '../../components/ui/richtext';
import { Select } from '../../components/ui/select';
import { TokenSelect } from '../../components/ui/tokenselect';
import { Skeleton } from '../../components/ui/skeleton';
import { Modal } from '../../components/ui/modal';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { ticketsApi } from '../../features/tickets/api';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';
import { useConfirm } from '../../lib/confirm';

interface AdminGroup {
  id: string;
  name: string;
}

interface CategoryNode extends CatalogueCategory {
  children: CategoryNode[];
  depth: number;
}

function buildTree(categories: CatalogueCategory[]): CategoryNode[] {
  const byParent = new Map<string | null, CatalogueCategory[]>();
  for (const c of categories) {
    const key = c.parentId ?? null;
    byParent.set(key, [...(byParent.get(key) ?? []), c]);
  }
  const build = (parentId: string | null, depth: number): CategoryNode[] =>
    (byParent.get(parentId) ?? []).map((c) => ({ ...c, depth, children: build(c.id, depth + 1) }));
  return build(null, 1);
}

/** A category plus every descendant — selecting a parent shows its whole
 *  subtree's items, like the reference tree does. */
function subtreeIds(node: CategoryNode): string[] {
  return [node.id, ...node.children.flatMap(subtreeIds)];
}

function flattenTree(nodes: CategoryNode[]): CategoryNode[] {
  return nodes.flatMap((n) => [n, ...flattenTree(n.children)]);
}

/**
 * Step 69 — has the visibility changed?
 *
 * Its own function because it is compared in two places (the dirty guard and
 * the save, which only PUTs when something actually moved) and the lists are
 * order-insensitive: adding then removing the same group must read as clean.
 */
/**
 * One visibility axis — Step 69.
 *
 * Agents and requesters are separate questions about the same item and the
 * control says so, side by side. A single "who can see this" picker would
 * have to pick one audience to mean, and the answer is different for each:
 * an item restricted to Finance requesters is still one the whole service
 * desk needs to be able to raise on their behalf.
 *
 * ── Empty means nobody, and it says so ─────────────────────────────────
 *
 * "Only these groups" with no group ticked fails closed — enforcement admits
 * nobody, which is the correct posture and a terrible surprise. So the
 * warning is on screen rather than in the audit log afterwards.
 */
function VisibilityAxis({
  title,
  hint,
  emptyHint,
  mode,
  groupIds,
  options,
  onChange,
}: {
  title: string;
  hint: string;
  emptyHint: string;
  mode: 'all' | 'groups';
  groupIds: string[];
  options: { id: string; name: string }[];
  onChange: (mode: 'all' | 'groups', ids: string[]) => void;
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs font-medium">{title}</p>
      <p className="mb-2 text-xs text-muted-foreground">{hint}</p>
      <div className="space-y-1.5">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="radio"
            name={`visibility-${title}`}
            checked={mode === 'all'}
            onChange={() => onChange('all', groupIds)}
            className="h-4 w-4 accent-[hsl(var(--brand))]"
          />
          Everyone
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="radio"
            name={`visibility-${title}`}
            checked={mode === 'groups'}
            onChange={() => onChange('groups', groupIds)}
            className="h-4 w-4 accent-[hsl(var(--brand))]"
          />
          Only these groups
        </label>
      </div>
      {mode === 'groups' && (
        <div className="mt-2 space-y-1.5 border-t pt-2">
          {options.length === 0 ? (
            <p className="text-xs text-muted-foreground">{emptyHint}</p>
          ) : (
            options.map((g) => (
              <label key={g.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={groupIds.includes(g.id)}
                  onChange={(e) =>
                    onChange(
                      'groups',
                      e.target.checked ? [...groupIds, g.id] : groupIds.filter((x) => x !== g.id),
                    )
                  }
                  className="h-4 w-4 accent-[hsl(var(--brand))]"
                />
                <span className="truncate">{g.name}</span>
              </label>
            ))
          )}
          {options.length > 0 && groupIds.length === 0 && (
            <p className="pt-1 text-xs text-amber-600 dark:text-amber-500">
              Nothing selected — nobody will see this item.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Upload an item icon — Step 73's last owed piece.
 *
 * Rides the media seam rather than adding a second path to disk: same
 * endpoint, same magic-byte sniffing, same org quota, same per-owner
 * authorisation. The purpose is `item_icon`, which is one of the two that
 * STRIPS metadata on the way in — an icon is artwork we publish, not evidence
 * somebody attached, and a logo dragged off a phone should not carry the
 * photographer's GPS into a customer's portal.
 *
 * Uploaded as `pending` and pointed at the item by the PATCH that saves the
 * draft, so abandoning the editor leaves a private orphan rather than
 * changing an item nobody meant to touch.
 */
function IconUpload({
  current,
  onUploaded,
  onClear,
}: {
  current: string | null;
  onUploaded: (id: string) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <button
        onClick={() => input.current?.click()}
        disabled={busy}
        title="Upload an image"
        aria-label="Upload an icon image"
        className="flex h-9 items-center gap-1.5 rounded-md border border-dashed px-2 text-xs text-muted-foreground hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Upload className="h-3.5 w-3.5" /> {busy ? 'Uploading…' : 'Upload'}
      </button>
      {current && (
        <button
          onClick={onClear}
          title="Remove the uploaded image"
          aria-label="Remove the uploaded icon"
          className="flex h-9 items-center gap-1.5 rounded-md border px-2 text-xs text-muted-foreground hover:border-destructive hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" /> Remove image
        </button>
      )}
      {error && <p className="w-full text-xs text-destructive">{error}</p>}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setError(null);
          setBusy(true);
          const body = new FormData();
          body.append('file', file);
          body.append('purpose', 'item_icon');
          // Bare fetch on purpose: apiSend serialises JSON, and a multipart
          // body must reach the network untouched so the browser sets its own
          // boundary.
          const res = await fetch('/api/media', { method: 'POST', body, credentials: 'same-origin' });
          setBusy(false);
          const json = (await res.json().catch(() => null)) as { id?: string; error?: string } | null;
          if (!res.ok || !json?.id) {
            // The server names the real reason and the real cap; repeating a
            // guess here is how a client ends up lying about the limit.
            setError(json?.error ?? 'That image could not be uploaded.');
            return;
          }
          onUploaded(json.id);
        }}
      />
    </>
  );
}

/**
 * "Additional items" — Step 77.
 *
 * The parts that come with this one. Each link is either MANDATORY (always
 * included, and the requester is TOLD rather than asked) or optional (offered
 * as a checkbox), and each can raise its own child service request so the
 * pieces are fulfilled and tracked separately.
 *
 * Saves on its own rather than joining the item's draft: it is a list with
 * its own whole-list-replace endpoint, and folding it into the item PATCH
 * would make an omitted key indistinguishable from an emptied bundle — the
 * same reason the visibility axes have their own route.
 */
function AdditionalItems({
  itemId,
  candidates,
}: {
  itemId: string;
  candidates: { id: string; name: string; icon: string | null }[];
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const links = useQuery({ queryKey: qk.itemLinks(itemId), queryFn: () => catalogueApi.itemLinks(itemId) });

  if (links.isPending || links.isError) return null;
  const rows = links.data.entries;

  const save = async (next: { childItemId: string; mandatory: boolean; createChildRequest: boolean }[]) => {
    setError(null);
    const res = await catalogueApi.setItemLinks(itemId, next);
    if (!res.ok) return setError(res.error ?? 'That could not be saved.');
    void qc.invalidateQueries({ queryKey: qk.itemLinks(itemId) });
  };
  const asInput = () =>
    rows.map((r) => ({
      childItemId: r.childItemId,
      mandatory: !!r.mandatory,
      createChildRequest: !!r.createChildRequest,
    }));

  const unlinked = candidates.filter((c) => !rows.some((r) => r.childItemId === c.id));

  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Additional items</label>
      {error && <p className="mb-2 text-xs text-destructive">{error}</p>}
      {rows.length > 0 && (
        <div className="mb-2 space-y-1.5">
          {rows.map((r, i) => (
            <div key={r.childItemId} className="flex flex-wrap items-center gap-3 rounded-md border p-2.5">
              <CatalogueIcon name={r.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm">{r.name}</span>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={!!r.mandatory}
                  onChange={(e) => {
                    const next = asInput();
                    next[i]!.mandatory = e.target.checked;
                    void save(next);
                  }}
                  className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                />
                Always included
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={!!r.createChildRequest}
                  onChange={(e) => {
                    const next = asInput();
                    next[i]!.createChildRequest = e.target.checked;
                    void save(next);
                  }}
                  className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                />
                Raises its own request
              </label>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${r.name}`}
                onClick={() => void save(asInput().filter((_, x) => x !== i))}
              >
                <Trash2 className="text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <Select
        value={adding}
        onChange={(e) => {
          const id = e.target.value;
          setAdding('');
          if (!id) return;
          void save([...asInput(), { childItemId: id, mandatory: false, createChildRequest: true }]);
        }}
      >
        <option value="">Add an item…</option>
        {unlinked.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
      <p className="mt-1 text-xs text-muted-foreground">
        Parts that come with this one. "Always included" is stated on the form rather than offered as a
        tick-box. Anything that raises its own request becomes a child ticket, and closing this one closes
        them.
      </p>
    </div>
  );
}

function visibilityDirty(
  draft: {
    agentVisibility: string;
    requesterVisibility: string;
    agentGroupIds: string[];
    requesterGroupIds: string[];
  },
  item: CatalogueItem,
): boolean {
  const same = (a: string[], b: string[]) => [...a].sort().join(',') === [...b].sort().join(',');
  return (
    draft.agentVisibility !== (item.agentVisibility ?? 'all') ||
    draft.requesterVisibility !== (item.requesterVisibility ?? 'all') ||
    !same(draft.agentGroupIds, item.agentGroupIds ?? []) ||
    !same(draft.requesterGroupIds, item.requesterGroupIds ?? [])
  );
}

export function CatalogueAdminPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const {
    data: categories,
    isPending: catPending,
    isError: catError,
    error: catErr,
    refetch: refetchCats,
  } = useQuery({ queryKey: qk.catalogueCategories, queryFn: catalogueApi.categories });
  const {
    data: items,
    isPending: itemsPending,
    isError: itemsError,
    error: itemsErr,
    refetch: refetchItems,
  } = useQuery({ queryKey: qk.catalogueItems.list('all'), queryFn: () => catalogueApi.items() });
  const { data: groupsData } = useQuery({
    queryKey: qk.adminGroups,
    queryFn: () => apiFetch<{ entries: AdminGroup[] }>('/api/admin/groups'),
  });
  const groups = groupsData?.entries ?? [];
  // Step 69 — the requester-side audiences an item can be restricted to.
  const requesterGroups = useQuery({
    queryKey: qk.requesterGroups,
    queryFn: catalogueApi.requesterGroups,
    staleTime: 5 * 60_000,
  });
  /**
   * Everyone who could approve — agents AND requesters.
   *
   * Kai: *"people approving might not be agents or admins etc"*. The person
   * signing off a laptop request is usually a line manager with no agent
   * licence and no reason to open the agent workspace at all; they answer
   * through the portal instead.
   */
  const approverPeople = useQuery({
    queryKey: qk.people,
    queryFn: ticketsApi.people,
    staleTime: 5 * 60_000,
  });
  // Step 101 — the boards an item may point at.
  const boards = useQuery({
    queryKey: qk.approvalBoardPicker,
    queryFn: () =>
      apiFetch<{ entries: { id: string; name: string; stages: { position: number }[] }[] }>(
        '/api/approvals/boards',
      ),
  });
  const approverOptions = (approverPeople.data?.entries ?? []).map((p) => ({
    value: p.id,
    label: p.name ?? p.email,
  }));

  // ── list state ──────────────────────────────────────────────────────────
  // "Every view has a URL" (the standard carried from digitaladmin). WHICH item
  // is open, WHICH category is selected, what you searched for and which status
  // you filtered to are all part of WHERE YOU ARE — in useState a refresh loses
  // your place and there is no way to send anyone a link to the item you are
  // looking at, which on a catalogue admin page is the main thing you want to do.
  const [selectedItemId, setSelectedItemId] = useUrlState('item', null);
  const [selectedCategoryId, setSelectedCategoryId] = useUrlState('category', null);
  const [search, setSearch] = useUrlState('q', '');
  const [statusFilterRaw, setStatusFilter] = useUrlState('status', 'all');
  const statusFilter: 'all' | 'published' | 'draft' =
    statusFilterRaw === 'published' || statusFilterRaw === 'draft' ? statusFilterRaw : 'all';
  // Step 68: selection for bulk ops, a page window, and manual ordering.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // The page window is a view too — "page 3 of the catalogue" should survive a
  // refresh. Stored 1-based in the URL because a `?page=0` reads as a bug.
  const [pageRaw, setPageRaw] = useUrlState('page', '1');
  const page = Math.max(0, (parseInt(pageRaw, 10) || 1) - 1);
  const setPage = (next: number | ((p: number) => number)) => {
    const value = typeof next === 'function' ? next(page) : next;
    setPageRaw(String(Math.max(0, value) + 1));
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkMoveIds, setBulkMoveIds] = useState<string[]>([]);
  const PAGE_SIZE = 25;
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [addingUnder, setAddingUnder] = useState<string | 'root' | null>(null);
  const [addCatName, setAddCatName] = useState('');
  const [renamingCatId, setRenamingCatId] = useState<string | null>(null);
  const [movingCatId, setMovingCatId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [catActionError, setCatActionError] = useState<string | null>(null);

  // ── editor state ────────────────────────────────────────────────────────
  const [draft, setDraft] = useState<{
    id: string;
    name: string;
    description: string;
    icon: string | null;
    categoryIds: string[];
    defaultGroupId: string | null;
    defaultPriority: string;
    /** Step 47 — none | manager | group | users. */
    approvalMode: string;
    approvalGroupId: string | null;
    approvalBoardId: string | null;
    approvalUserIds: string[];
    subjectTemplate: string;
    isPopular: boolean;
    descriptionHtml: string;
    cost: string;
    costVisible: boolean;
    deliveryTime: string;
    deliveryVisible: boolean;
    /** Step 72 — offer the "for someone else" control on this item. */
    allowOnBehalf: boolean;
    /** Step 73 — may this request carry files, and must it. */
    allowAttachments: boolean;
    requireAttachment: boolean;
    /** Step 73 — an uploaded icon, in front of the lucide `icon` name. */
    iconAttachmentId: string | null;
    /** Step 69 — who may see and request this, per audience. */
    agentVisibility: 'all' | 'groups';
    requesterVisibility: 'all' | 'groups';
    agentGroupIds: string[];
    requesterGroupIds: string[];
  } | null>(null);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const layoutRef = useRef<FormLayoutEditorHandle>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.catalogueItems.all });
    void queryClient.invalidateQueries({ queryKey: qk.catalogueCategories });
  };

  const createItem = useMutation({
    mutationFn: (body: { name: string; categoryId?: string }) => catalogueApi.createItem(body),
    onSuccess: (res) => {
      if (res.ok && res.body) {
        // Selecting the new item is a URL change, so the guard is retired
        // first — the name field it was guarding has just been saved.
        clearDirty();
        setNewItemName('');
        setCreatingItem(false);
        invalidate();
        setSelectedItemId(res.body.id);
      }
    },
  });

  const selectedItem = items?.entries.find((it) => it.id === selectedItemId) ?? null;
  const activeDraft =
    selectedItem && draft?.id === selectedItem.id
      ? draft
      : selectedItem
        ? {
            id: selectedItem.id,
            name: selectedItem.name,
            description: selectedItem.description ?? '',
            icon: selectedItem.icon,
            categoryIds:
              selectedItem.categoryIds ?? (selectedItem.categoryId ? [selectedItem.categoryId] : []),
            defaultGroupId: selectedItem.defaultGroupId,
            approvalMode: selectedItem.approvalMode ?? 'none',
            approvalGroupId: selectedItem.approvalGroupId ?? null,
            approvalBoardId: selectedItem.approvalBoardId ?? null,
            approvalUserIds: (selectedItem.approvalUserIds as string[] | null) ?? [],
            defaultPriority: selectedItem.defaultPriority ?? '',
            subjectTemplate: selectedItem.subjectTemplate ?? '',
            isPopular: !!selectedItem.isPopular,
            descriptionHtml: selectedItem.descriptionHtml ?? '',
            cost: selectedItem.cost ?? '',
            costVisible: !!selectedItem.costVisible,
            deliveryTime: selectedItem.deliveryTime ?? '',
            deliveryVisible: !!selectedItem.deliveryVisible,
            allowOnBehalf: !!selectedItem.allowOnBehalf,
            allowAttachments: !!selectedItem.allowAttachments,
            requireAttachment: !!selectedItem.requireAttachment,
            iconAttachmentId: selectedItem.iconAttachmentId ?? null,
            agentVisibility: selectedItem.agentVisibility ?? 'all',
            requesterVisibility: selectedItem.requesterVisibility ?? 'all',
            agentGroupIds: selectedItem.agentGroupIds ?? [],
            requesterGroupIds: selectedItem.requesterGroupIds ?? [],
          }
        : null;
  const detailsDirty =
    !!selectedItem &&
    !!activeDraft &&
    (activeDraft.name !== selectedItem.name ||
      activeDraft.description !== (selectedItem.description ?? '') ||
      activeDraft.icon !== selectedItem.icon ||
      [...activeDraft.categoryIds].sort().join(',') !==
        [...(selectedItem.categoryIds ?? (selectedItem.categoryId ? [selectedItem.categoryId] : []))]
          .sort()
          .join(',') ||
      activeDraft.defaultGroupId !== selectedItem.defaultGroupId ||
      activeDraft.approvalMode !== (selectedItem.approvalMode ?? 'none') ||
      activeDraft.approvalGroupId !== (selectedItem.approvalGroupId ?? null) ||
      activeDraft.approvalBoardId !== (selectedItem.approvalBoardId ?? null) ||
      JSON.stringify(activeDraft.approvalUserIds) !==
        JSON.stringify((selectedItem.approvalUserIds as string[] | null) ?? []) ||
      activeDraft.defaultPriority !== (selectedItem.defaultPriority ?? '') ||
      activeDraft.subjectTemplate !== (selectedItem.subjectTemplate ?? '') ||
      activeDraft.isPopular !== !!selectedItem.isPopular ||
      activeDraft.descriptionHtml !== (selectedItem.descriptionHtml ?? '') ||
      activeDraft.cost !== (selectedItem.cost ?? '') ||
      activeDraft.costVisible !== !!selectedItem.costVisible ||
      activeDraft.deliveryTime !== (selectedItem.deliveryTime ?? '') ||
      activeDraft.deliveryVisible !== !!selectedItem.deliveryVisible ||
      activeDraft.allowOnBehalf !== !!selectedItem.allowOnBehalf ||
      activeDraft.allowAttachments !== !!selectedItem.allowAttachments ||
      activeDraft.requireAttachment !== !!selectedItem.requireAttachment ||
      activeDraft.iconAttachmentId !== (selectedItem.iconAttachmentId ?? null) ||
      visibilityDirty(activeDraft, selectedItem));
  const anyDirty = detailsDirty || layoutDirty;
  const clearDirty = useDirty(anyDirty);

  const tree = useMemo(() => buildTree(categories?.entries ?? []), [categories]);
  const flat = useMemo(() => flattenTree(tree), [tree]);

  if (catError || itemsError) {
    const message = ((catErr ?? itemsErr) as Error | null)?.message ?? 'Failed to load';
    return (
      <div className="m-8 max-w-xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-4">
        <p className="text-sm font-medium text-destructive">Could not load the service catalogue</p>
        <p className="text-xs text-destructive/80">{message}</p>
        <button
          onClick={() => {
            void refetchCats();
            void refetchItems();
          }}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  }
  if (catPending || itemsPending)
    return (
      <div className="flex gap-6 p-8">
        <div className="w-64 shrink-0 space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
        <div className="flex-1 space-y-3">
          <Skeleton className="h-10 w-full" />
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );

  // ═══════════════════════════ EDITOR VIEW ═══════════════════════════════
  if (selectedItem && activeDraft) {
    const published = !!selectedItem.isActive;

    async function saveAll(mode: 'keep' | 'publish' | 'draft') {
      setSaveMenuOpen(false);
      const name = activeDraft!.name.trim();
      if (!name || saving) return;
      setSaving(true);
      setSaveError(null);
      let layoutId: string | null = selectedItem!.formLayoutId;
      if (layoutDirty || !layoutId) {
        layoutId = (await layoutRef.current?.save(name)) ?? null;
        if (!layoutId) {
          setSaving(false);
          setSaveError('The form could not be saved — check the section conditions and try again.');
          return;
        }
      }
      const res = await catalogueApi.updateItem(selectedItem!.id, {
        name,
        description: activeDraft!.description,
        icon: activeDraft!.icon || null,
        categoryIds: activeDraft!.categoryIds,
        defaultGroupId: activeDraft!.defaultGroupId || null,
        approvalMode: activeDraft!.approvalMode,
        approvalGroupId: activeDraft!.approvalMode === 'group' ? activeDraft!.approvalGroupId : null,
        approvalUserIds: activeDraft!.approvalMode === 'users' ? activeDraft!.approvalUserIds : null,
        defaultPriority: activeDraft!.defaultPriority || null,
        subjectTemplate: activeDraft!.subjectTemplate.trim() || null,
        isPopular: activeDraft!.isPopular,
        allowOnBehalf: activeDraft!.allowOnBehalf ? 1 : 0,
        allowAttachments: activeDraft!.allowAttachments ? 1 : 0,
        // Required is meaningless without allowed, and the server treats the
        // pair that way too — sending it clean keeps the DB honest rather
        // than storing a flag that only `attachmentPolicy` knows to ignore.
        requireAttachment: activeDraft!.allowAttachments && activeDraft!.requireAttachment ? 1 : 0,
        iconAttachmentId: activeDraft!.iconAttachmentId,
        descriptionHtml: activeDraft!.descriptionHtml,
        cost: activeDraft!.cost,
        costVisible: activeDraft!.costVisible,
        deliveryTime: activeDraft!.deliveryTime,
        deliveryVisible: activeDraft!.deliveryVisible,
        formLayoutId: layoutId,
        ...(mode === 'publish' ? { isActive: true } : mode === 'draft' ? { isActive: false } : {}),
      });
      if (!res.ok) {
        setSaving(false);
        setSaveError(res.error ?? 'Saving failed');
        return;
      }
      /**
       * Visibility goes as its own PUT, and only when it moved.
       *
       * Two writes behind one button, which is worth stating: the axes are
       * whole-list replaces and the item PATCH is a partial update, so
       * folding them together would make an omitted key indistinguishable
       * from an emptied list. The guard means the ordinary save — a renamed
       * item, a reworded description — still makes exactly one call.
       */
      if (visibilityDirty(activeDraft!, selectedItem!)) {
        const vis = await catalogueApi.setVisibility(selectedItem!.id, {
          agentVisibility: activeDraft!.agentVisibility,
          requesterVisibility: activeDraft!.requesterVisibility,
          agentGroupIds: activeDraft!.agentGroupIds,
          requesterGroupIds: activeDraft!.requesterGroupIds,
        });
        if (!vis.ok) {
          setSaving(false);
          // The item itself saved; say so rather than implying nothing landed.
          setSaveError(vis.error ?? 'The item saved, but its visibility did not.');
          return;
        }
      }
      setSaving(false);
      setDraft(null);
      invalidate();
    }

    return (
      <div className="flex h-full min-h-0 flex-col">
        {/* Sticky action header — the reference's item header, our tokens. */}
        <div className="sticky top-0 z-20 flex shrink-0 flex-wrap items-center gap-3 border-b bg-background/95 px-6 py-3 backdrop-blur">
          <button
            onClick={() => setSelectedItemId(null)}
            className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Back to catalogue"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <CatalogueIcon
            name={activeDraft.icon}
            attachmentId={activeDraft.iconAttachmentId}
            className="h-5 w-5 text-muted-foreground"
          />
          <h1 className="min-w-0 max-w-md truncate text-base font-semibold">
            {activeDraft.name || 'Untitled item'}
          </h1>
          {published ? (
            <Badge variant="outline" className="gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Published
            </Badge>
          ) : (
            <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
              Draft
            </Badge>
          )}
          <nav className="ml-2 hidden items-center gap-3 text-xs text-muted-foreground md:flex">
            <a href="#item-general" className="hover:text-foreground">
              General
            </a>
            <a href="#item-form" className="hover:text-foreground">
              Request form
            </a>
            <a href="#item-settings" className="hover:text-foreground">
              Settings
            </a>
          </nav>
          <div className="flex-1" />
          {anyDirty && <span className="text-xs text-amber-600 dark:text-amber-500">Unsaved changes</span>}
          {saveError && <span className="max-w-xs truncate text-xs text-destructive">{saveError}</span>}
          <div className="relative flex">
            <Button
              size="sm"
              className="rounded-r-none"
              disabled={saving || !activeDraft.name.trim() || (published && !anyDirty)}
              onClick={() => void saveAll(published ? 'keep' : 'publish')}
            >
              {published ? 'Save' : 'Save & Publish'}
            </Button>
            <Button
              size="sm"
              className="rounded-l-none border-l border-primary-foreground/20 px-1.5"
              disabled={saving}
              onClick={() => setSaveMenuOpen((o) => !o)}
              aria-label="More save options"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            {saveMenuOpen && (
              <div className="absolute right-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-md border bg-popover py-1 shadow-md">
                <button
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => void saveAll('publish')}
                >
                  Save &amp; Publish
                </button>
                <button
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => void saveAll('draft')}
                >
                  Save as Draft
                </button>
                <button
                  className="flex w-full items-center gap-2 border-t px-3 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => {
                    setSaveMenuOpen(false);
                    setSaveAsName(`Copy of ${activeDraft.name}`);
                    setSaveAsOpen(true);
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> Save As…
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {/* ── General details ─────────────────────────────────────────── */}
          <section id="item-general" className="mb-8 scroll-mt-16">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              General details
            </h2>
            <div className="rounded-lg border bg-card p-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-[auto_minmax(0,1fr)_240px]">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Icon</label>
                  <button
                    onClick={() => setIconPickerOpen((o) => !o)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Choose an icon"
                  >
                    <CatalogueIcon
                      name={activeDraft.icon}
                      attachmentId={activeDraft.iconAttachmentId}
                      className="h-5 w-5"
                    />
                  </button>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Item name</label>
                  <Input
                    value={activeDraft.name}
                    onChange={(e) => setDraft({ ...activeDraft, name: e.target.value })}
                    className="h-10 text-base font-semibold"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Categories</label>
                  {/* Checklist, not a single Select — an item can sit in more
                      than one folder (Kai). First tick = the primary. */}
                  <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-1.5">
                    {flat.length === 0 && (
                      <p className="px-1 py-0.5 text-xs text-muted-foreground">No categories yet.</p>
                    )}
                    {flat.map((c) => {
                      const on = activeDraft.categoryIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-sm hover:bg-accent/60"
                          style={{ paddingLeft: `${(c.depth - 1) * 14 + 6}px` }}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() =>
                              setDraft({
                                ...activeDraft,
                                categoryIds: on
                                  ? activeDraft.categoryIds.filter((id) => id !== c.id)
                                  : [...activeDraft.categoryIds, c.id],
                              })
                            }
                            className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                          />
                          <span className="truncate">{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              {iconPickerOpen && (
                <div className="mt-3 flex flex-wrap gap-1.5 rounded-md border bg-muted/10 p-2.5">
                  {CATALOGUE_ICONS.map(({ name, Icon }) => (
                    <button
                      key={name}
                      onClick={() => {
                        // Picking a drawn icon CLEARS the upload — otherwise
                        // the upload keeps winning and the click looks broken.
                        setDraft({ ...activeDraft, icon: name, iconAttachmentId: null });
                        setIconPickerOpen(false);
                      }}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        activeDraft.icon === name && 'border-brand bg-brand/10 text-brand',
                      )}
                      aria-label={name}
                      title={name}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                  <IconUpload
                    current={activeDraft.iconAttachmentId}
                    onUploaded={(id) => {
                      setDraft({ ...activeDraft, iconAttachmentId: id });
                      setIconPickerOpen(false);
                    }}
                    onClear={() => setDraft({ ...activeDraft, iconAttachmentId: null })}
                  />
                </div>
              )}
              <div className="mt-5">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Short description
                </label>
                <Input
                  value={activeDraft.description}
                  onChange={(e) => setDraft({ ...activeDraft, description: e.target.value })}
                  placeholder="One line, shown on the browse card"
                  className="h-10"
                />
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Full description
                </label>
                <RichTextEditor
                  value={activeDraft.descriptionHtml}
                  onChange={(v) => setDraft({ ...activeDraft, descriptionHtml: v })}
                  placeholder="Shown on the request page itself — what this covers, what to have ready…"
                />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Cost</label>
                  <Input
                    value={activeDraft.cost}
                    onChange={(e) => setDraft({ ...activeDraft, cost: e.target.value })}
                    placeholder="e.g. £250 or Recharged to department"
                    className="h-10"
                  />
                  <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={activeDraft.costVisible}
                      onChange={(e) => setDraft({ ...activeDraft, costVisible: e.target.checked })}
                      className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                    />
                    Show this to requesters
                  </label>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Estimated delivery
                  </label>
                  <Input
                    value={activeDraft.deliveryTime}
                    onChange={(e) => setDraft({ ...activeDraft, deliveryTime: e.target.value })}
                    placeholder="e.g. 3 working days"
                    className="h-10"
                  />
                  <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={activeDraft.deliveryVisible}
                      onChange={(e) => setDraft({ ...activeDraft, deliveryVisible: e.target.checked })}
                      className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                    />
                    Show this to requesters
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* ── Request form (the builder) ──────────────────────────────── */}
          <section id="item-form" className="mb-8 scroll-mt-16">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Request form
              </h2>
              <Link
                to="/admin/fields"
                className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <ListPlus className="h-3.5 w-3.5" /> Manage the shared field library
              </Link>
            </div>
            <FormLayoutEditor
              ref={layoutRef}
              layoutId={selectedItem.formLayoutId}
              onDirtyChange={setLayoutDirty}
            />
          </section>

          {/* ── Settings ────────────────────────────────────────────────── */}
          <section id="item-settings" className="scroll-mt-16 pb-16">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Settings
            </h2>
            <div className="grid grid-cols-1 gap-5 rounded-lg border bg-card p-5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Auto-assign to group
                </label>
                <Select
                  value={activeDraft.defaultGroupId ?? ''}
                  onChange={(e) => setDraft({ ...activeDraft, defaultGroupId: e.target.value || null })}
                  className="h-10"
                >
                  <option value="">No default group</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Requests from this item land in this group's queue.
                </p>
              </div>
              {/* Step 47 — the approval this item ALWAYS needs. Kai:
                  *"automatic approvals for service requests in their config,
                  like request manager approval, or approval from this
                  group."* Before this, an approval existed only because an
                  agent remembered to ask, so the requests that most need a
                  gate were the ones that slipped through on a busy
                  afternoon. */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Needs approval
                </label>
                <Select
                  value={activeDraft.approvalMode}
                  onChange={(e) => setDraft({ ...activeDraft, approvalMode: e.target.value })}
                  className="h-10"
                >
                  <option value="none">No approval</option>
                  <option value="manager">The requester's line manager</option>
                  <option value="group">Anyone in a group</option>
                  <option value="users">Named people</option>
                  <option value="board">An approval board</option>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {activeDraft.approvalMode === 'manager'
                    ? 'Read from the directory. A requester with no manager on record cannot submit this item, and is told why.'
                    : activeDraft.approvalMode === 'none'
                      ? 'Requests go straight to the queue.'
                      : activeDraft.approvalMode === 'board'
                        ? 'A board can have several stages, asked in order. Manage boards under Settings → Approval boards.'
                        : 'One approval is enough — the request waits until somebody says yes.'}
                </p>
              </div>
              {activeDraft.approvalMode === 'board' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Board</label>
                  <Select
                    value={activeDraft.approvalBoardId ?? ''}
                    onChange={(e) => setDraft({ ...activeDraft, approvalBoardId: e.target.value || null })}
                    className="h-10"
                  >
                    <option value="">Choose a board…</option>
                    {(boards.data?.entries ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.stages.length} stage{b.stages.length === 1 ? '' : 's'})
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {activeDraft.approvalMode === 'group' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Approving group
                  </label>
                  <Select
                    value={activeDraft.approvalGroupId ?? ''}
                    onChange={(e) => setDraft({ ...activeDraft, approvalGroupId: e.target.value || null })}
                    className="h-10"
                  >
                    <option value="">Choose a group…</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {activeDraft.approvalMode === 'users' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Approvers</label>
                  {/* Requesters included, not just agents. Kai: *"people
                      approving might not be agents or admins"* — the person
                      signing off a laptop is usually a line manager with no
                      agent licence. */}
                  <TokenSelect
                    value={activeDraft.approvalUserIds}
                    onChange={(v) => setDraft({ ...activeDraft, approvalUserIds: v })}
                    ariaLabel="Approvers"
                    options={approverOptions}
                  />
                </div>
              )}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Default priority
                </label>
                <Select
                  value={activeDraft.defaultPriority}
                  onChange={(e) => setDraft({ ...activeDraft, defaultPriority: e.target.value })}
                  className="h-10"
                >
                  <option value="">Medium (default)</option>
                  <option value="low">Low</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  A leaver request shouldn't triage like a stationery one.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Popular items shelf
                </label>
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border px-3">
                  <input
                    type="checkbox"
                    checked={activeDraft.isPopular}
                    onChange={(e) => setDraft({ ...activeDraft, isPopular: e.target.checked })}
                    className="h-4 w-4 accent-[hsl(var(--brand))]"
                  />
                  <span className="text-sm">Show in Popular items</span>
                </label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Hand-picked — the default shelf on both browse pages.
                </p>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Ticket subject template
                </label>
                <Input
                  value={activeDraft.subjectTemplate}
                  onChange={(e) => setDraft({ ...activeDraft, subjectTemplate: e.target.value })}
                  placeholder="{{item.name}} - {{field.req_first_name}} {{field.req_surname}}"
                  className="h-10 font-mono text-xs"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Tokens: <code>{'{{item.name}}'}</code>, <code>{'{{requester.name}}'}</code>,{' '}
                  <code>{'{{field.<key>}}'}</code> — resolved from the answers when a request is raised; the
                  subject becomes deterministic and the requester never types one.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Attachments</label>
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border px-3">
                  <input
                    type="checkbox"
                    checked={activeDraft.allowAttachments}
                    onChange={(e) =>
                      setDraft({
                        ...activeDraft,
                        allowAttachments: e.target.checked,
                        // Turning files off turns the requirement off with
                        // it. Leaving it set would be an item that demands a
                        // file it offers no way to attach — unsubmittable,
                        // with nothing on screen explaining why.
                        requireAttachment: e.target.checked && activeDraft.requireAttachment,
                      })
                    }
                    className="h-4 w-4 accent-[hsl(var(--brand))]"
                  />
                  <span className="text-sm">Allow files</span>
                </label>
                {activeDraft.allowAttachments && (
                  <label className="mt-1.5 flex h-10 cursor-pointer items-center gap-2 rounded-md border px-3">
                    <input
                      type="checkbox"
                      checked={activeDraft.requireAttachment}
                      onChange={(e) => setDraft({ ...activeDraft, requireAttachment: e.target.checked })}
                      className="h-4 w-4 accent-[hsl(var(--brand))]"
                    />
                    <span className="text-sm">Require at least one</span>
                  </label>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Up to 15 MB each. Refused types are named in the error, not silently dropped.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Requested for
                </label>
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border px-3">
                  <input
                    type="checkbox"
                    checked={activeDraft.allowOnBehalf}
                    onChange={(e) => setDraft({ ...activeDraft, allowOnBehalf: e.target.checked })}
                    className="h-4 w-4 accent-[hsl(var(--brand))]"
                  />
                  <span className="text-sm">Can be requested for someone else</span>
                </label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Adds the standard person picker to the form. Whoever is chosen becomes the requester; the
                  person who raised it is recorded and Cc'd.
                </p>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <AdditionalItems
                  itemId={selectedItem.id}
                  candidates={(items?.entries ?? []).filter((i) => i.id !== selectedItem.id)}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Visibility</label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <VisibilityAxis
                    title="Requesters"
                    hint="Who can see and request this on the portal."
                    emptyHint="No requester groups yet — create one under Settings › Requester groups."
                    mode={activeDraft.requesterVisibility}
                    groupIds={activeDraft.requesterGroupIds}
                    options={(requesterGroups.data?.entries ?? []).map((g) => ({ id: g.id, name: g.name }))}
                    onChange={(mode, ids) =>
                      setDraft({ ...activeDraft!, requesterVisibility: mode, requesterGroupIds: ids })
                    }
                  />
                  <VisibilityAxis
                    title="Agents"
                    hint="Who can see and raise this from the agent side."
                    emptyHint="No agent groups yet — create one under Settings › Agents and groups."
                    mode={activeDraft.agentVisibility}
                    groupIds={activeDraft.agentGroupIds}
                    options={groups.map((g) => ({ id: g.id, name: g.name }))}
                    onChange={(mode, ids) =>
                      setDraft({ ...activeDraft!, agentVisibility: mode, agentGroupIds: ids })
                    }
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Save As (clone) — the reference's dialog: prefilled name, lands as a draft. */}
        <Modal open={saveAsOpen} onClose={() => setSaveAsOpen(false)} title="Save As">
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Item name</label>
              <Input autoFocus value={saveAsName} onChange={(e) => setSaveAsName(e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">
                Copies this item and its whole form. The copy starts as a draft.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSaveAsOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!saveAsName.trim()}
                onClick={async () => {
                  const res = await catalogueApi.cloneItem(selectedItem.id, saveAsName.trim());
                  if (res.ok && res.body) {
                    setSaveAsOpen(false);
                    setDraft(null);
                    setLayoutDirty(false);
                    invalidate();
                    setSelectedItemId(res.body.id);
                  }
                }}
              >
                Create copy
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // ═══════════════════════════ LIST VIEW ═════════════════════════════════
  const selectedNode = flat.find((c) => c.id === selectedCategoryId) ?? null;
  const categoryFilterIds = selectedNode ? new Set(subtreeIds(selectedNode)) : null;
  const q = search.trim().toLowerCase();
  const visibleItems = (items?.entries ?? []).filter((it) => {
    if (categoryFilterIds && !(it.categoryIds ?? []).some((cid) => categoryFilterIds.has(cid))) return false;
    if (statusFilter === 'published' && !it.isActive) return false;
    if (statusFilter === 'draft' && it.isActive) return false;
    if (q && !it.name.toLowerCase().includes(q) && !(it.description ?? '').toLowerCase().includes(q))
      return false;
    return true;
  });
  const itemCountIn = (node: CategoryNode) => {
    const ids = new Set(subtreeIds(node));
    return (items?.entries ?? []).filter((it) => (it.categoryIds ?? []).some((cid) => ids.has(cid))).length;
  };
  const categoryName = (id: string | null) => flat.find((c) => c.id === id)?.name ?? 'Uncategorised';
  const nodeHeight = (n: CategoryNode): number =>
    n.children.length ? 1 + Math.max(...n.children.map(nodeHeight)) : 1;

  async function submitAddCategory() {
    const name = addCatName.trim();
    if (!name) return;
    setCatActionError(null);
    const res = await catalogueApi.createCategory(
      name,
      addingUnder === 'root' ? undefined : (addingUnder ?? undefined),
    );
    if (res.ok) {
      setAddCatName('');
      setAddingUnder(null);
      invalidate();
    } else setCatActionError(res.error ?? 'Could not create the category');
  }

  function CategoryRow({ node }: { node: CategoryNode }) {
    const active = selectedCategoryId === node.id;
    return (
      <div>
        <div
          className={cn(
            'group flex items-center gap-1 rounded-md py-1 pr-1 text-sm',
            active ? 'bg-accent font-medium text-accent-foreground' : 'hover:bg-accent/60',
          )}
          style={{ paddingLeft: `${(node.depth - 1) * 14 + 8}px` }}
        >
          {renamingCatId === node.id ? (
            <Input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Escape') setRenamingCatId(null);
                if (e.key === 'Enter' && renameValue.trim()) {
                  await catalogueApi.updateCategory(node.id, { name: renameValue.trim() });
                  setRenamingCatId(null);
                  invalidate();
                }
              }}
              onBlur={() => setRenamingCatId(null)}
              className="h-7 flex-1 text-sm"
            />
          ) : (
            <>
              <button
                onClick={() => setSelectedCategoryId(active ? null : node.id)}
                className="min-w-0 flex-1 truncate rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {node.name}
              </button>
              <span className="shrink-0 text-xs text-muted-foreground">{itemCountIn(node)}</span>
              <span className="hidden shrink-0 items-center gap-0.5 group-hover:flex">
                <button
                  onClick={() => {
                    setRenamingCatId(node.id);
                    setRenameValue(node.name);
                  }}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Rename ${node.name}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {node.depth < 3 && (
                  <button
                    onClick={() => {
                      setAddingUnder(node.id);
                      setAddCatName('');
                    }}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Add subcategory under ${node.name}`}
                    title="Create subcategory"
                  >
                    <FolderPlus className="h-3 w-3" />
                  </button>
                )}
                <button
                  onClick={() => setMovingCatId(movingCatId === node.id ? null : node.id)}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Move ${node.name} under another category`}
                  title="Move into another folder"
                >
                  <FolderInput className="h-3 w-3" />
                </button>
                <button
                  onClick={async () => {
                    setCatActionError(null);
                    const res = await catalogueApi.deleteCategory(node.id);
                    if (res.ok) {
                      if (selectedCategoryId === node.id) setSelectedCategoryId(null);
                      invalidate();
                    } else setCatActionError(res.error ?? 'Could not delete');
                  }}
                  className="rounded p-0.5 text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Delete ${node.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            </>
          )}
        </div>
        {movingCatId === node.id && (
          <div className="flex items-center gap-1 py-1" style={{ paddingLeft: `${node.depth * 14 + 8}px` }}>
            <Select
              autoFocus
              className="h-7 flex-1 text-sm"
              value=""
              onChange={async (e) => {
                const target = e.target.value;
                setMovingCatId(null);
                setCatActionError(null);
                const res = await catalogueApi.updateCategory(node.id, {
                  parentId: target === 'root' ? null : target,
                });
                if (res.ok) invalidate();
                else setCatActionError(res.error ?? 'Could not move the category');
              }}
              onBlur={() => setMovingCatId(null)}
            >
              <option value="" disabled>
                Move “{node.name}” to…
              </option>
              {node.depth > 1 && <option value="root">Top level</option>}
              {flat
                .filter(
                  (c) =>
                    c.id !== node.id &&
                    !subtreeIds(node).includes(c.id) &&
                    c.id !== (node as { parentId?: string | null }).parentId &&
                    c.depth + nodeHeight(node) <= 3,
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {'\u00A0'.repeat((c.depth - 1) * 3)}
                    {c.name}
                  </option>
                ))}
            </Select>
          </div>
        )}
        {addingUnder === node.id && (
          <div className="flex items-center gap-1 py-1" style={{ paddingLeft: `${node.depth * 14 + 8}px` }}>
            <Input
              autoFocus
              value={addCatName}
              onChange={(e) => setAddCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitAddCategory();
                if (e.key === 'Escape') setAddingUnder(null);
              }}
              onBlur={() => {
                if (!addCatName.trim()) setAddingUnder(null);
              }}
              placeholder="Subcategory name"
              className="h-7 flex-1 text-sm"
            />
          </div>
        )}
        {node.children.map((c) => (
          <CategoryRow key={c.id} node={c} />
        ))}
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  // Narrowing the filters can strand the window past the end of a shorter
  // list — an empty table that looks like "no results" but isn't.
  if (page > 0 && page >= pageCount) setPage(0);
  const pageItems = visibleItems.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageIds = pageItems.map((it: CatalogueItem) => it.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  async function afterBulk(res: {
    ok: boolean;
    body?: { affected: number; blocked?: { name: string }[] } | null;
    error?: string | null;
  }) {
    setBulkBusy(false);
    if (!res.ok) {
      setBulkNote(res.error ?? 'That bulk action failed.');
      return;
    }
    const blocked = res.body?.blocked ?? [];
    setBulkNote(
      blocked.length
        ? `${res.body?.affected ?? 0} deleted. Kept ${blocked.length}: ${blocked
            .map((b) => b.name)
            .join(
              ', ',
            )} — tickets were raised from these, so deleting them would orphan those tickets. Move them to drafts instead.`
        : null,
    );
    setSelectedIds(new Set());
    setBulkMoveOpen(false);
    invalidate();
  }
  async function runBulkStatus(isActive: boolean) {
    setBulkBusy(true);
    setBulkNote(null);
    await afterBulk(await catalogueApi.bulk({ ids: [...selectedIds], action: 'status', isActive }));
  }
  async function runBulkMove() {
    setBulkBusy(true);
    setBulkNote(null);
    await afterBulk(
      await catalogueApi.bulk({ ids: [...selectedIds], action: 'move', categoryIds: bulkMoveIds }),
    );
  }
  async function runBulkDelete() {
    if (
      !(await confirm({
        title: `Permanently delete ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}?`,
        body: 'Items that already have tickets raised from them are kept automatically.',
        destructive: true,
        confirmLabel: 'Delete',
      }))
    )
      return;
    setBulkBusy(true);
    setBulkNote(null);
    await afterBulk(await catalogueApi.bulk({ ids: [...selectedIds], action: 'delete' }));
  }
  /** Manual order writes sort_order by position across the WHOLE filtered
   *  list, not just this page — otherwise paging would silently renumber. */
  async function moveItem(id: string, delta: number) {
    const ids = visibleItems.map((it: CatalogueItem) => it.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    setBulkBusy(true);
    const res = await catalogueApi.setItemOrder(ids);
    setBulkBusy(false);
    if (res.ok) invalidate();
    else setBulkNote(res.error ?? 'Could not reorder');
  }

  return (
    // Full bleed on purpose: a split pane; padding here would inset the divider.
    <div className="flex h-full min-h-0">
      {/* Category tree — the reference's sidebar, in-page (not app chrome). */}
      <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-r p-3">
        <button
          onClick={() => setSelectedCategoryId(null)}
          className={cn(
            'mb-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedCategoryId === null
              ? 'bg-accent font-medium text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent/60',
          )}
        >
          <LayoutGrid className="h-4 w-4" /> All service items
          <span className="ml-auto text-xs text-muted-foreground">{items?.entries.length ?? 0}</span>
        </button>
        <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Categories
        </p>
        {tree.map((node) => (
          <CategoryRow key={node.id} node={node} />
        ))}
        {addingUnder === 'root' ? (
          <div className="mt-1 flex items-center gap-1 px-2">
            <Input
              autoFocus
              value={addCatName}
              onChange={(e) => setAddCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitAddCategory();
                if (e.key === 'Escape') setAddingUnder(null);
              }}
              placeholder="Category name"
              className="h-7 flex-1 text-sm"
            />
          </div>
        ) : (
          <button
            onClick={() => {
              setAddingUnder('root');
              setAddCatName('');
            }}
            className="mt-1 flex items-center gap-1 rounded px-2 py-1 text-left text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-3.5 w-3.5" /> New category
          </button>
        )}
        {catActionError && <p className="mt-2 px-2 text-xs text-destructive">{catActionError}</p>}
      </aside>

      {/* Item table */}
      <main className="min-h-0 flex-1 overflow-y-auto p-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold">{selectedNode ? selectedNode.name : 'Service catalogue'}</h1>
          <div className="flex-1" />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service items"
              className="h-9 w-64 pl-8"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="h-9 w-36"
          >
            <option value="all">All items</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </Select>
          <Button
            size="sm"
            onClick={() => {
              setCreatingItem((o) => !o);
              setNewItemName('');
            }}
          >
            <Plus /> New service item
          </Button>
        </div>

        {creatingItem && (
          <div className="mb-4 flex items-center gap-2 rounded-md border bg-muted/20 p-3">
            <Input
              autoFocus
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newItemName.trim() && !createItem.isPending)
                  createItem.mutate({
                    name: newItemName.trim(),
                    ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
                  });
                if (e.key === 'Escape') setCreatingItem(false);
              }}
              placeholder={
                selectedNode ? `Item name — created in ${selectedNode.name}` : 'Item name (e.g. Leaver)'
              }
              className="h-9 flex-1"
            />
            <Button
              size="sm"
              disabled={!newItemName.trim() || createItem.isPending}
              onClick={() =>
                createItem.mutate({
                  name: newItemName.trim(),
                  ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
                })
              }
            >
              Create
            </Button>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand/30 bg-brand/5 p-2.5 text-sm">
            <span className="font-medium">
              {selectedIds.size} item{selectedIds.size === 1 ? '' : 's'} selected
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              disabled={bulkBusy}
              onClick={async () => {
                setBulkMoveIds([]);
                setBulkMoveOpen(true);
              }}
            >
              Move to category
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => void runBulkStatus(true)}>
              Publish
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => void runBulkStatus(false)}>
              Move to drafts
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              disabled={bulkBusy}
              onClick={() => void runBulkDelete()}
            >
              <Trash2 /> Delete
            </Button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
        )}
        {bulkNote && (
          <p className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400">
            {bulkNote}
          </p>
        )}
        {bulkMoveOpen && (
          <div className="mb-3 space-y-2 rounded-md border bg-muted/20 p-3">
            <p className="text-xs font-medium text-muted-foreground">
              Move {selectedIds.size} item{selectedIds.size === 1 ? '' : 's'} into…
            </p>
            <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border bg-background p-1.5">
              {flat.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-sm hover:bg-accent/60"
                  style={{ paddingLeft: `${(c.depth - 1) * 14 + 6}px` }}
                >
                  <input
                    type="checkbox"
                    checked={bulkMoveIds.includes(c.id)}
                    onChange={() =>
                      setBulkMoveIds((prev) =>
                        prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id],
                      )
                    }
                    className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                  />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setBulkMoveOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={bulkBusy} onClick={() => void runBulkMove()}>
                {bulkMoveIds.length === 0 ? 'Remove from all categories' : `Move to ${bulkMoveIds.length}`}
              </Button>
            </div>
          </div>
        )}

        {visibleItems.length === 0 ? (
          <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
            {q || statusFilter !== 'all' || selectedNode
              ? 'Nothing matches this filter.'
              : 'No catalogue items yet — create the first one above.'}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="grid grid-cols-[28px_minmax(0,1fr)_180px_110px_76px] gap-2 border-b bg-muted/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span className="flex items-center">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allOnPageSelected}
                  onChange={() =>
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
                      else pageIds.forEach((id) => next.add(id));
                      return next;
                    })
                  }
                  className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                />
              </span>
              <span>Item</span>
              <span>Category</span>
              <span>Status</span>
              <span className="text-right">Order</span>
            </div>
            {pageItems.map((it: CatalogueItem) => (
              <div
                key={it.id}
                className="group grid cursor-pointer grid-cols-[28px_minmax(0,1fr)_180px_110px_76px] items-center gap-2 border-b px-4 py-2.5 last:border-b-0 hover:bg-accent/40"
                onClick={() => setSelectedItemId(it.id)}
                role="row"
              >
                <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${it.name}`}
                    checked={selectedIds.has(it.id)}
                    onChange={() =>
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(it.id)) next.delete(it.id);
                        else next.add(it.id);
                        return next;
                      })
                    }
                    className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                  />
                </span>
                <span className="flex min-w-0 items-center gap-2.5">
                  <CatalogueIcon
                    name={it.icon}
                    attachmentId={it.iconAttachmentId}
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{it.name}</span>
                    {it.description && (
                      <span className="block truncate text-xs text-muted-foreground">{it.description}</span>
                    )}
                  </span>
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {(it.categoryIds ?? []).length > 1
                    ? `${categoryName(it.categoryIds[0] ?? null)} +${it.categoryIds.length - 1}`
                    : categoryName(it.categoryIds?.[0] ?? it.categoryId ?? null)}
                </span>
                <span>
                  {it.isActive ? (
                    <Badge variant="outline" className="gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Published
                    </Badge>
                  ) : (
                    <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                      Draft
                    </Badge>
                  )}
                </span>
                <span className="flex items-center justify-end gap-0.5">
                  {/* Arrows, not drag: the same reorder idiom the options
                      editor already uses, and keyboard-reachable. */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void moveItem(it.id, -1);
                    }}
                    disabled={bulkBusy}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Move ${it.name} up`}
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void moveItem(it.id, 1);
                    }}
                    disabled={bulkBusy}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Move ${it.name} down`}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const res = await catalogueApi.cloneItem(it.id);
                      if (res.ok && res.body) {
                        invalidate();
                        setSelectedItemId(res.body.id);
                      }
                    }}
                    className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                    aria-label={`Clone ${it.name}`}
                    title="Clone"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {visibleItems.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min(visibleItems.length, (page + 1) * PAGE_SIZE)} of{' '}
              {visibleItems.length}
            </span>
            <button
              onClick={async () => {
                if (
                  !(await confirm({
                    title: 'Reset every item to alphabetical order?',
                    confirmLabel: 'Reset order',
                  }))
                )
                  return;
                setBulkBusy(true);
                const res = await catalogueApi.setItemOrder([]);
                setBulkBusy(false);
                if (res.ok) invalidate();
              }}
              className="text-brand hover:underline"
            >
              Reset to A–Z
            </button>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
